import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { BadgeCheck, MoreHorizontal, Trash2 } from 'lucide-react';
import { useRef } from 'react';

import styles from './QuestionActionsMenu.module.css';

interface QuestionActionsMenuProps {
  // Offered unless every student already has full credit on the question.
  canGiveFullCredit: boolean;
  // Identifies this question's control, for returning focus to it.
  controlKey: string;
  onGiveFullCredit(): void;
  onRemove(): void;
  questionName: string;
}

/**
 * A question's class-wide actions on the gradebook's assignment page. Each one
 * opens a confirmation dialog. It opens only once the menu has finished
 * closing, so the menu's focus return cannot pull focus back out of the
 * dialog.
 */
export function QuestionActionsMenu({
  canGiveFullCredit,
  controlKey,
  onGiveFullCredit,
  onRemove,
  questionName,
}: QuestionActionsMenuProps) {
  const chosenAction = useRef<(() => void) | null>(null);

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          aria-label={`Actions for ${questionName}`}
          className={styles.trigger}
          data-question-control={controlKey}
          type="button"
        >
          <MoreHorizontal aria-hidden="true" size={20} strokeWidth={2} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className={styles.menuContent}
          onCloseAutoFocus={(event) => {
            const action = chosenAction.current;
            if (action) {
              chosenAction.current = null;
              event.preventDefault();
              action();
            }
          }}
          sideOffset={6}
        >
          {canGiveFullCredit ? (
            <>
              <DropdownMenu.Item
                className={styles.menuItem}
                onSelect={() => {
                  chosenAction.current = onGiveFullCredit;
                }}
              >
                <BadgeCheck aria-hidden="true" size={18} />
                <span>Give everyone full credit</span>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className={styles.separator} />
            </>
          ) : null}
          <DropdownMenu.Item
            className={[styles.menuItem, styles.dangerMenuItem].join(' ')}
            onSelect={() => {
              chosenAction.current = onRemove;
            }}
          >
            <Trash2 aria-hidden="true" size={18} />
            <span>Remove from assignment</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
